import { api } from './api';
import { Inbox } from '../types/inbox';
import {
  AgentBot,
  AssignAgentBotToInboxRequest,
  CreateAgentBotRequest,
  UpdateAgentBotRequest,
} from '../types/agentBot';
import {
  BotActuationConfiguration,
  UpdateBotActuationRequest,
  UpdateBotInboxActuationRequest,
} from '../types/botActuation';
import { LeadClassificationTag } from '../types/leadClassificationTag';
import { ClassificationSchema } from '../types/classificationSchema';

export interface AgentBotSignature {
  messageSignature: string;
  imageUrl?: string | null;
}

class AgentBotService {
  async getAll(): Promise<AgentBot[]> {
    const response = await api.get<AgentBot[]>('/agent-bots');
    return response.data;
  }

  async getById(id: string): Promise<AgentBot> {
    const response = await api.get<AgentBot>(`/agent-bots/${id}`);
    return response.data;
  }

  async create(data: CreateAgentBotRequest): Promise<AgentBot> {
    const response = await api.post<AgentBot>('/agent-bots', data);
    return response.data;
  }

  async update(id: string, data: UpdateAgentBotRequest): Promise<AgentBot> {
    const response = await api.put<AgentBot>(`/agent-bots/${id}`, data);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/agent-bots/${id}`);
  }

  async assignToInbox(id: string, data: AssignAgentBotToInboxRequest): Promise<Inbox> {
    const response = await api.post<Inbox>(`/agent-bots/${id}/assign-inbox`, data);
    return response.data;
  }

  async getActuation(id: string, inboxId?: string): Promise<BotActuationConfiguration> {
    const params = inboxId ? { inboxId } : undefined;
    const response = await api.get<BotActuationConfiguration>(`/agent-bots/${id}/actuation`, { params });
    return response.data;
  }

  async updateActuation(id: string, data: UpdateBotActuationRequest): Promise<BotActuationConfiguration> {
    const response = await api.put<BotActuationConfiguration>(`/agent-bots/${id}/actuation`, data);
    return response.data;
  }

  async updateInboxActuation(id: string, data: UpdateBotInboxActuationRequest): Promise<BotActuationConfiguration> {
    const response = await api.put<BotActuationConfiguration>(`/agent-bots/${id}/actuation/inbox`, data);
    return response.data;
  }

  async getLeadClassificationTags(id: string): Promise<LeadClassificationTag[]> {
    const response = await api.get<LeadClassificationTag[]>(
      `/agent-bots/${id}/lead-classification-tags`
    );
    return response.data;
  }

  async getClassificationSchema(id: string): Promise<ClassificationSchema> {
    const response = await api.get<ClassificationSchema>(
      `/agent-bots/${id}/classification-schema`
    );
    return response.data;
  }

  async updateClassificationSchema(id: string, schema: ClassificationSchema): Promise<ClassificationSchema> {
    const response = await api.put<ClassificationSchema>(
      `/agent-bots/${id}/classification-schema`,
      schema
    );
    return response.data;
  }

  async resetClassificationSchema(id: string): Promise<ClassificationSchema> {
    const response = await api.post<ClassificationSchema>(
      `/agent-bots/${id}/classification-schema/reset-defaults`
    );
    return response.data;
  }

  async getSignature(id: string): Promise<AgentBotSignature> {
    const response = await api.get<AgentBotSignature>(`/agent-bots/${id}/signature`);
    return response.data;
  }

  async updateSignature(
    id: string,
    messageSignature: string,
    image?: File | null,
    removeImage = false,
  ): Promise<AgentBotSignature> {
    const formData = new FormData();
    formData.append('messageSignature', messageSignature);
    formData.append('removeImage', String(removeImage));

    if (image)
    {
      formData.append('image', image);
    }

    const response = await api.put<AgentBotSignature>(`/agent-bots/${id}/signature`, formData);
    return response.data;
  }
}

export const agentBotService = new AgentBotService();

